import { useState, useRef, useEffect } from 'react';
import { CheckCircle2, Clock, ListChecks, Maximize2, Minimize2, Pin, StickyNote, Trash2, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  type Note,
  type NotesScope,
  useNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
  useConvertNoteToTask,
  useNoteHistory,
} from '../../lib/notes.js';
import { useAuth } from '../../hooks/useAuth.js';

function noteLabel(note: Pick<Note, 'body'>) {
  return note.body.trim().split('\n')[0]?.slice(0, 60) || 'Untitled note';
}

function canUseTeamScope(user: { role?: string | null; roles?: string[] | null; isSuperAdmin?: boolean | null } | null | undefined) {
  return Boolean(
    user
      && (user.role === 'founder'
        || user.isSuperAdmin === true
        || user.roles?.includes('super_admin') === true),
  );
}

function QuickNoteCard({
  note,
  onUpdate,
  onDelete,
  onConvert,
  updating,
  deleting,
  converting,
}: {
  note: Note;
  onUpdate: (patch: {
    id: string;
    body?: string;
    pinned?: boolean;
    status?: 'open' | 'done' | 'archived';
  }) => void;
  onDelete: (id: string) => void;
  onConvert: (id: string) => void;
  updating?: boolean;
  deleting?: boolean;
  converting?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyQuery = useNoteHistory(note.id, historyOpen);
  const label = noteLabel(note);

  useEffect(() => {
    if (!editing) setDraft(note.body);
  }, [editing, note.body]);

  const saveEdit = () => {
    const body = draft.trim();
    if (!body || updating) return;
    onUpdate({ id: note.id, body });
    setEditing(false);
  };

  return (
    <li
      className={`rounded-xl border p-3 shadow-sm transition-colors ${
        note.status === 'done'
          ? 'border-line bg-surface-sunken/80 opacity-80'
          : note.pinned
            ? 'border-primary/25 bg-primary/5'
            : 'border-[#ddd2b6] bg-[#fbf7ed] dark:border-[#49412f] dark:bg-[#201d16]'
      }`}
    >
      <div className="mb-2 flex items-center gap-2 text-[10px] text-ink-subtle">
        <StickyNote size={12} className={note.pinned ? 'text-primary' : 'text-ink-subtle'} />
        <span className="font-semibold uppercase tracking-wide">{note.status === 'done' ? 'Done' : 'Open'}</span>
        {note.pinned && <span className="rounded-full bg-primary/10 px-1.5 py-0.5 font-medium text-primary">Pinned</span>}
        {note.dealThreadId && <span className="rounded-full bg-surface-sunken px-1.5 py-0.5">Linked deal</span>}
        <span className="ml-auto">{formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}</span>
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={5}
            className="max-h-[52vh] min-h-32 w-full resize-y rounded-lg border border-line bg-surface px-3 py-2 text-sm leading-relaxed text-ink outline-none transition focus:border-primary/40 focus:ring-2 focus:ring-primary/25"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-muted hover:bg-surface-sunken hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={!draft.trim() || updating}
              className="rounded-lg bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-fg disabled:opacity-40"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`block max-h-32 w-full overflow-y-auto rounded-lg px-1 py-0.5 text-left text-sm leading-relaxed text-ink transition-colors hover:bg-surface/70 ${
            note.status === 'done' ? 'line-through' : ''
          }`}
          aria-label={`Edit ${label}`}
        >
          <span className="whitespace-pre-wrap">{note.body}</span>
        </button>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => onUpdate({ id: note.id, pinned: !note.pinned })}
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors hover:bg-surface-sunken ${
            note.pinned ? 'text-primary' : 'text-ink-muted hover:text-ink'
          }`}
          aria-label={note.pinned ? `Unpin ${label}` : `Pin ${label}`}
        >
          <Pin size={12} />
          {note.pinned ? 'Pinned' : 'Pin'}
        </button>
        <button
          type="button"
          onClick={() => onUpdate({ id: note.id, status: note.status === 'done' ? 'open' : 'done' })}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
          aria-label={note.status === 'done' ? `Reopen ${label}` : `Mark ${label} done`}
        >
          <CheckCircle2 size={12} />
          {note.status === 'done' ? 'Reopen' : 'Done'}
        </button>
        {note.convertedTaskId ? (
          <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-primary">
            <ListChecks size={12} />
            Task linked
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onConvert(note.id)}
            disabled={converting}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-ink-muted transition-colors hover:bg-surface-sunken hover:text-primary disabled:opacity-40"
            aria-label={`Convert ${label} to task`}
          >
            <ListChecks size={12} />
            Task
          </button>
        )}
        <button
          type="button"
          onClick={() => setHistoryOpen((value) => !value)}
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors hover:bg-surface-sunken ${
            historyOpen ? 'text-primary' : 'text-ink-muted hover:text-ink'
          }`}
          aria-label={historyOpen ? `Hide history for ${label}` : `Show history for ${label}`}
        >
          <Clock size={12} />
          History
        </button>
        <button
          type="button"
          onClick={() => onDelete(note.id)}
          disabled={deleting}
          className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-ink-subtle transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-40"
          aria-label={`Delete ${label}`}
        >
          <Trash2 size={12} />
          Delete
        </button>
      </div>

      {historyOpen && (
        <div className="mt-2 rounded-lg border border-line bg-surface/80 px-3 py-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Version history</div>
          {historyQuery.isLoading ? (
            <div className="h-8 rounded-md shimmer" />
          ) : (historyQuery.data?.entries ?? []).length === 0 ? (
            <p className="text-xs text-ink-subtle">No history recorded yet.</p>
          ) : (
            <ul className="space-y-1">
              {(historyQuery.data?.entries ?? []).map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-2 text-xs text-ink-muted">
                  <span>{entry.action.replace(/^note\./, '').replace(/_/g, ' ')}</span>
                  <span className="text-[11px] text-ink-subtle">{formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

export default function QuickNotesButton() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [notesScope, setNotesScope] = useState<NotesScope>('mine');
  const ref = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const canUseTeamNotesScope = canUseTeamScope(user);
  const effectiveNotesScope: NotesScope | undefined = canUseTeamNotesScope && notesScope === 'all' ? 'all' : undefined;

  useEffect(() => {
    if (!canUseTeamNotesScope && notesScope !== 'mine') setNotesScope('mine');
  }, [canUseTeamNotesScope, notesScope]);

  const notesQuery = useNotes(effectiveNotesScope ? { scope: effectiveNotesScope } : {}, open);
  const recent = (notesQuery.data?.notes ?? []).slice(0, 8);
  const createNote = useCreateNote();
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();
  const convertNote = useConvertNoteToTask();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const save = () => {
    const b = body.trim();
    if (!b || createNote.isPending) return;
    createNote.mutate({ body: b }, { onSuccess: () => setBody('') });
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-pressed={open}
        aria-label={open ? 'Close notes' : 'Open notes'}
        title="Quick note"
        className={`flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors duration-150 ${
          open ? 'bg-brand/10 text-brand' : 'text-ink-muted hover:bg-surface-raised hover:text-ink'
        }`}
      >
        <StickyNote size={15} />
        <span className="hidden md:inline">Note</span>
      </button>

      {open && (
        <div className="fixed inset-x-3 top-16 z-50 flex max-h-[calc(100vh-5rem)] w-auto flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl sm:absolute sm:inset-auto sm:right-0 sm:top-10 sm:w-[min(96vw,34rem)] sm:max-h-[min(82vh,46rem)]">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <div>
              <span className="text-sm font-semibold text-ink">Live notes</span>
              <p className="text-xs text-ink-subtle">Capture now, review past notes, and turn notes into tasks.</p>
            </div>
            <div className="flex items-center gap-1">
              {canUseTeamNotesScope && (
                <button
                  type="button"
                  onClick={() => setNotesScope((scope) => (scope === 'all' ? 'mine' : 'all'))}
                  aria-pressed={notesScope === 'all'}
                  className={`rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${
                    notesScope === 'all'
                      ? 'bg-brand/10 text-brand'
                      : 'text-ink-muted hover:bg-surface-sunken hover:text-ink'
                  }`}
                >
                  All notes
                </button>
              )}
              <button
                onClick={() => setExpanded((value) => !value)}
                className="rounded-md p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink"
                title={expanded ? 'Compact composer' : 'Expand composer'}
                aria-label={expanded ? 'Compact composer' : 'Expand composer'}
              >
                {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-1 text-ink-muted hover:bg-surface-sunken hover:text-ink"
                title="Close"
                aria-label="Close notes"
              >
                <X size={14} />
              </button>
            </div>
          </div>
          <div className="border-b border-[#ddd2b6] bg-[#f5f0e3] p-4 dark:border-[#49412f] dark:bg-[#262218]">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={expanded ? 10 : 4}
              placeholder="Write a note... Capture the operator thought, next step, or decision."
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  save();
                }
              }}
              className="max-h-[56vh] min-h-32 w-full resize-y rounded-xl border border-[#d8cda9] bg-[#fffaf0] px-3 py-2 text-sm leading-relaxed text-[#332c1d] shadow-inner placeholder:text-[#88795d] focus:border-[#bda96d] focus:outline-none focus:ring-2 focus:ring-[#d9c47a]/40 dark:border-[#4c452f] dark:bg-[#1f1c16] dark:text-[#f3ead4] dark:placeholder:text-[#9b9077]"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2 text-[10px] text-ink-subtle">
                <span>{body.length.toLocaleString()} chars</span>
                <span className="rounded-full bg-surface/70 px-2 py-0.5">Global note</span>
                <span className="rounded-full bg-surface/70 px-2 py-0.5">Cmd Enter saves</span>
              </div>
              <button
                onClick={save}
                disabled={!body.trim() || createNote.isPending}
                className="rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40"
              >
                {createNote.isPending ? 'Saving...' : 'Save note'}
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Past notes</p>
              <span className="text-[10px] text-ink-subtle">{recent.length} shown</span>
            </div>
            {notesQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-16 rounded-lg shimmer" />
                ))}
              </div>
            ) : recent.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line px-3 py-8 text-center text-xs text-ink-muted">
                No notes yet.
              </div>
            ) : (
              <ul className="space-y-2">
                {recent.map((note) => (
                  <QuickNoteCard
                    key={note.id}
                    note={note}
                    onUpdate={(patch) => updateNote.mutate(patch)}
                    onDelete={(id) => deleteNote.mutate(id)}
                    onConvert={(id) => convertNote.mutate(id)}
                    updating={updateNote.isPending}
                    deleting={deleteNote.isPending}
                    converting={convertNote.isPending}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
